import { useEffect, useState, useRef } from "react";
import { useCountUp } from "@/lib/useCountUp";
import { CalendarCheck, Flame, ShieldCheck, Sparkles, TrendingUp } from "lucide-react";

// ---------- Deduction Efficiency Meter (circular SVG ring) ----------
export function DeductionEfficiencyMeter({ stats }) {
  const deductible = Number(stats?.deductible || 0);
  const gross = Number(stats?.total_spent || 0);
  const efficiency = gross > 0 ? Math.min(1, deductible / gross) : 0;
  const pct = Math.round(efficiency * 100);
  const animated = useCountUp(pct, 900);
  const size = 132;
  const stroke = 12;
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const dash = circ * (animated / 100);
  const tint = pct >= 60 ? "#10B981" : pct >= 30 ? "#f59e0b" : "#f43f5e";
  return (
    <div
      data-testid="deduction-efficiency-meter"
      className="glass rounded-2xl p-5 fade-up-widget flex flex-col items-center text-center"
      style={{ animationDelay: "0ms" }}
    >
      <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-emerald-400 mb-2 flex items-center gap-1.5">
        <Sparkles size={11} /> Deduction Efficiency
      </div>
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="rotate-[-90deg]">
          <circle cx={size / 2} cy={size / 2} r={r} strokeWidth={stroke} stroke="rgba(148,163,184,0.15)" fill="none" />
          <circle
            cx={size / 2} cy={size / 2} r={r} strokeWidth={stroke} stroke={tint} fill="none"
            strokeLinecap="round" strokeDasharray={`${dash} ${circ}`}
            style={{ transition: "stroke 400ms ease", filter: `drop-shadow(0 0 8px ${tint}90)` }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="text-white font-bold text-3xl font-mono" style={{ color: tint }} data-testid="deduction-efficiency-value">
            {Math.round(animated)}%
          </div>
          <div className="text-[9px] font-mono uppercase tracking-widest text-slate-500 mt-0.5">of gross</div>
        </div>
      </div>
      <div className="mt-3 text-[11px] text-slate-400 leading-tight">
        <span className="text-emerald-300 font-mono">${deductible.toFixed(0)}</span> deducted / <span className="font-mono">${gross.toFixed(0)}</span> spent
      </div>
    </div>
  );
}


// ---------- Burn Rate Odometer (multi-stage gradient bar) ----------
export function BurnRateOdometer({ stats, receipts, budgetCap = 10000 }) {
  // Prefer server-computed MTD from /api/stats/summary (full dataset). Fall back to
  // summing the `recent` array only if the stats field is missing (dev/test paths).
  const monthKey = new Date().toISOString().slice(0, 7);
  const fallbackFromRecent = (receipts || []).reduce((acc, r) => {
    const d = (r.date || r.created_at || "").slice(0, 7);
    return d === monthKey ? acc + Number(r.total || 0) : acc;
  }, 0);
  const statsMonth = Number(stats?.month_spent || 0);
  const spend = statsMonth > 0 ? statsMonth : fallbackFromRecent;
  const pct = Math.max(0, Math.min(1, spend / (budgetCap || 1)));
  const animatedSpend = useCountUp(spend, 900);
  const animatedPct = useCountUp(pct * 100, 900);
  const stage = pct < 0.6 ? "Healthy" : pct < 0.85 ? "Watch" : "Critical";
  const stageColor = pct < 0.6 ? "text-emerald-300" : pct < 0.85 ? "text-yellow-300" : "text-rose-300";
  return (
    <div
      data-testid="burn-rate-odometer"
      className="glass rounded-2xl p-5 fade-up-widget flex flex-col"
      style={{ animationDelay: "80ms" }}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-emerald-400 flex items-center gap-1.5">
          <TrendingUp size={11} /> Burn Rate · MTD
        </div>
        <span data-testid="burn-rate-stage" className={`text-[10px] font-mono uppercase tracking-widest ${stageColor}`}>{stage}</span>
      </div>
      <div className="mt-1">
        <div className="flex items-baseline gap-2">
          <div className="text-white font-mono text-3xl font-bold" data-testid="burn-rate-value">${animatedSpend.toFixed(0)}</div>
          <div className="text-slate-500 text-xs font-mono">/ ${budgetCap.toLocaleString()} cap</div>
        </div>
        <div className="mt-3 h-3 rounded-full bg-white/[0.04] overflow-hidden relative border border-white/5">
          <div
            className="h-full bg-gradient-to-r from-emerald-500 via-yellow-500 to-rose-500 transition-[width] duration-700 ease-out"
            style={{
              width: `${Math.min(100, animatedPct)}%`,
              boxShadow: pct >= 0.85 ? "0 0 12px rgba(244,63,94,0.6)" : "0 0 10px rgba(16,185,129,0.35)",
            }}
          />
          {/* stage markers */}
          <span className="absolute top-0 bottom-0 w-px bg-white/10" style={{ left: "60%" }} />
          <span className="absolute top-0 bottom-0 w-px bg-white/10" style={{ left: "85%" }} />
        </div>
        <div className="mt-2 flex items-center justify-between text-[10px] font-mono uppercase tracking-widest text-slate-500">
          <span>0</span><span>60%</span><span>85%</span><span>Cap</span>
        </div>
      </div>
      <div className="mt-3 text-[11px] text-slate-400">
        <span data-testid="burn-rate-pct" className="text-white font-mono">{Math.round(animatedPct)}%</span> of monthly operating budget
      </div>
    </div>
  );
}


// ---------- CPA Readiness Check (segmented progress) ----------
export function CpaReadinessCheck({ receipts }) {
  const list = receipts || [];
  const total = list.length;
  const complete = list.filter((r) => {
    // A receipt is "CPA-ready" when the critical fields are populated (not null / empty)
    const hasMerchant = !!(r.merchant && String(r.merchant).trim());
    const hasTotal = Number(r.total || 0) > 0;
    const hasDate = !!(r.date || r.created_at);
    const hasCategory = !!(r.category && String(r.category).trim());
    return hasMerchant && hasTotal && hasDate && hasCategory;
  }).length;
  const ratio = total > 0 ? complete / total : 1;
  const pct = Math.round(ratio * 100);
  const animated = useCountUp(pct, 900);
  const segments = 12;
  const filled = Math.round((animated / 100) * segments);
  const label = pct >= 90 ? "CPA-ready" : pct >= 60 ? "Nearly ready" : "Missing fields";
  const labelColor = pct >= 90 ? "text-emerald-300" : pct >= 60 ? "text-yellow-300" : "text-rose-300";
  return (
    <div
      data-testid="cpa-readiness-check"
      className="glass rounded-2xl p-5 fade-up-widget flex flex-col"
      style={{ animationDelay: "160ms" }}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-emerald-400 flex items-center gap-1.5">
          <ShieldCheck size={11} /> CPA Readiness
        </div>
        <span data-testid="cpa-readiness-label" className={`text-[10px] font-mono uppercase tracking-widest ${labelColor}`}>{label}</span>
      </div>
      <div className="flex items-baseline gap-2">
        <div className="text-white font-mono text-3xl font-bold" data-testid="cpa-readiness-value">{Math.round(animated)}%</div>
        <div className="text-slate-500 text-xs font-mono">{complete}/{total || 0} receipts</div>
      </div>
      <div className="mt-3 flex gap-1" data-testid="cpa-readiness-segments">
        {Array.from({ length: segments }).map((_, i) => (
          <div
            key={i}
            className="flex-1 h-3 rounded-sm transition-colors duration-300"
            style={{
              background: i < filled
                ? (pct >= 90 ? "#10B981" : pct >= 60 ? "#eab308" : "#f43f5e")
                : "rgba(148,163,184,0.15)",
              boxShadow: i < filled ? (pct >= 90 ? "0 0 6px rgba(16,185,129,0.7)" : "none") : "none",
            }}
          />
        ))}
      </div>
      <div className="mt-3 text-[11px] text-slate-400">
        {total - complete > 0
          ? <><span className="text-rose-300 font-mono">{total - complete}</span> receipts missing merchant, category or date</>
          : "All receipts complete — ready to hand off to your CPA"}
      </div>
    </div>
  );
}


